import { produce } from 'immer';
import { ChatAction, ChatActionTypes } from './actions';
import { Attachment } from './context';

export interface ChatState {
  threadables: {
    byType: {
      room: {
        byId: Record<string, Groupthink.RoomResource>;
        allIds: string[]; // Ordered by timestamp
        loading: boolean;
        validating: boolean;
        error: string | null;
      };
      task: {
        byId: Record<string, Groupthink.TaskResource>;
        allIds: string[]; // Ordered by timestamp
        loading: boolean;
        validating: boolean;
        error: string | null;
      };
    };
  };

  threads: {
    byThreadableId: Record<
      string,
      {
        byId: Record<string, Groupthink.ThreadResource>;
        allIds: string[]; // Ordered by timestamp
        loading: boolean;
        validating: boolean;
        error: string | null;
        hasMorePages: boolean;
      }
    >;
    unreadThreadsByThreadableId: Record<string, string[]>;
  };

  messages: {
    byThreadId: Record<
      string,
      {
        byId: Record<string, Groupthink.MessageResource>;
        allIds: string[]; // Ordered by timestamp
        loading: boolean;
        validating: boolean;
        error: string | null;
      }
    >;
  };
}

export const initialChatState: ChatState = {
  threadables: {
    byType: {
      room: {
        byId: {},
        allIds: [],
        loading: false,
        validating: false,
        error: null,
      },
      task: {
        byId: {},
        allIds: [],
        loading: false,
        validating: false,
        error: null,
      },
    },
  },
  threads: {
    byThreadableId: {},
    unreadThreadsByThreadableId: {},
  },
  messages: {
    byThreadId: {},
  },
};

export function reducer(state: ChatState = initialChatState, action: ChatAction): ChatState {
  return produce(state, (draft) => {
    switch (action.type) {
      case ChatActionTypes.SET_THREADABLES_LOADING: {
        const { threadableType, isLoading } = action;
        if (!draft.threadables.byType[threadableType]) {
          draft.threadables.byType[threadableType] = {
            byId: {},
            allIds: [],
            loading: isLoading,
            validating: false,
            error: null,
          };
        } else {
          draft.threadables.byType[threadableType].loading = isLoading;
          draft.threadables.byType[threadableType].error = null;
        }
        break;
      }

      case ChatActionTypes.SET_THREADABLES_VALIDATING: {
        const { threadableType, isValidating } = action;
        if (!draft.threadables.byType[threadableType]) {
          draft.threadables.byType[threadableType] = {
            byId: {},
            allIds: [],
            loading: false,
            validating: isValidating,
            error: null,
          };
        } else {
          draft.threadables.byType[threadableType].validating = isValidating;
          draft.threadables.byType[threadableType].error = null;
        }
        break;
      }

      case ChatActionTypes.SET_THREADABLES_DATA: {
        const { threadableType, threadables } = action;

        // Create a normalized map of threadables by ID
        const threadablesById = threadables.reduce(
          (acc, threadable) => {
            acc[threadable.id] = threadable;
            return acc;
          },
          {} as Record<
            string,
            Groupthink.RoomResource | Groupthink.TaskResource | Groupthink.AgendaResource
          >
        );

        // Initialize the threadable type if it doesn't exist
        if (!draft.threadables.byType[threadableType]) {
          draft.threadables.byType[threadableType] = {
            byId: {},
            allIds: [],
            loading: false,
            validating: false,
            error: null,
          };
        }

        // Update the threadables state
        draft.threadables.byType[threadableType].byId = threadablesById;
        draft.threadables.byType[threadableType].allIds = threadables.map(
          (threadable) => threadable.id
        );
        draft.threadables.byType[threadableType].loading = false;
        draft.threadables.byType[threadableType].validating = false;
        draft.threadables.byType[threadableType].error = null;
        break;
      }

      case ChatActionTypes.SET_THREADABLES_ERROR: {
        const { threadableType, error } = action;

        if (!draft.threadables.byType[threadableType]) {
          draft.threadables.byType[threadableType] = {
            byId: {},
            allIds: [],
            loading: false,
            validating: false,
            error: error.message,
          };
        } else {
          draft.threadables.byType[threadableType].loading = false;
          draft.threadables.byType[threadableType].validating = false;
          draft.threadables.byType[threadableType].error = error.message;
        }
        break;
      }

      case ChatActionTypes.DELETE_THREADABLE: {
        const { threadableType, threadableId } = action.payload;

        delete draft.threadables.byType[threadableType].byId[threadableId];
        delete draft.threads.byThreadableId[threadableId];

        break;
      }

      case ChatActionTypes.SET_THREADS_LOADING: {
        const { threadableId, isLoading } = action;
        if (!draft.threads.byThreadableId[threadableId]) {
          draft.threads.byThreadableId[threadableId] = {
            byId: {},
            allIds: [],
            hasMorePages: false,
            loading: isLoading,
            validating: false,
            error: null,
          };
        } else {
          draft.threads.byThreadableId[threadableId].loading = isLoading;
          draft.threads.byThreadableId[threadableId].error = null;
        }
        break;
      }

      case ChatActionTypes.SET_THREADS_VALIDATING: {
        const { threadableId, isValidating } = action;
        if (!draft.threads.byThreadableId[threadableId]) {
          draft.threads.byThreadableId[threadableId] = {
            byId: {},
            allIds: [],
            hasMorePages: false,
            loading: false,
            validating: isValidating,
            error: null,
          };
        } else {
          draft.threads.byThreadableId[threadableId].validating = isValidating;
          draft.threads.byThreadableId[threadableId].error = null;
        }
        break;
      }

      case ChatActionTypes.SET_THREADS_DATA: {
        const { threadableId, threads, hasMorePages } = action;

        const threadsById = threads.reduce(
          (acc, thread) => {
            acc[thread.id] = thread;
            return acc;
          },
          {} as Record<string, Groupthink.ThreadResource>
        );

        const allThreadIds = Object.values(threadsById)
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          .map((thread) => thread.id);

        // Keep any existing threads with tempId (optimistically added threads)
        const currentThreads = draft.threads.byThreadableId[threadableId]?.byId || {};
        const tempThreads = Object.entries(currentThreads)
          .filter(([id]) => id.startsWith('temp-'))
          .reduce(
            (acc, [id, thread]) => {
              acc[id] = thread;
              return acc;
            },
            {} as Record<string, Groupthink.ThreadResource>
          );

        // Keep tempIds in allIds
        const currentAllIds = draft.threads.byThreadableId[threadableId]?.allIds || [];
        const tempThreadIds = currentAllIds.filter((id) => id.startsWith('temp-'));

        draft.threads.byThreadableId[threadableId] = {
          byId: { ...tempThreads, ...threadsById }, // Merge temp threads with server threads
          allIds: [...tempThreadIds, ...allThreadIds], // Keep temp thread IDs and add server thread IDs
          loading: false,
          validating: false,
          error: null,
          hasMorePages,
        };

        // Store oldest_message of each thread in the messages state
        threads.forEach((thread) => {
          if (thread.oldest_message) {
            const threadId = thread.id;
            const message = thread.oldest_message;

            // Initialize messages for this thread if needed
            if (!draft.messages.byThreadId[threadId]) {
              draft.messages.byThreadId[threadId] = {
                byId: {},
                allIds: [],
                loading: false,
                validating: false,
                error: null,
              };
            }

            // Add the oldest message to the thread's messages
            draft.messages.byThreadId[threadId].byId[message.id] = message;

            // Update allIds array if the message isn't already in it
            if (!draft.messages.byThreadId[threadId].allIds.includes(message.id)) {
              draft.messages.byThreadId[threadId].allIds.push(message.id);
              // Sort messages by timestamp
              draft.messages.byThreadId[threadId].allIds.sort((a, b) => {
                const msgA = draft.messages.byThreadId[threadId].byId[a];
                const msgB = draft.messages.byThreadId[threadId].byId[b];
                return new Date(msgA.created_at).getTime() - new Date(msgB.created_at).getTime();
              });
            }
          }
        });

        break;
      }

      case ChatActionTypes.SET_THREADS_ERROR: {
        const { threadableId, error } = action;
        if (!draft.threads.byThreadableId[threadableId]) {
          draft.threads.byThreadableId[threadableId] = {
            byId: {},
            allIds: [],
            hasMorePages: false,
            loading: false,
            validating: false,
            error: error.message,
          };
        } else {
          draft.threads.byThreadableId[threadableId].loading = false;
          draft.threads.byThreadableId[threadableId].validating = false;
          draft.threads.byThreadableId[threadableId].error = error.message;
        }
        break;
      }
      case ChatActionTypes.MARK_THREAD_AS_READ: {
        const { threadableId, threadId, messageId } = action.payload;
        const unreadThreads = draft.threads.unreadThreadsByThreadableId[threadableId] || [];

        if (draft.threads.byThreadableId[threadableId]?.byId[threadId]) {
          draft.threads.byThreadableId[threadableId].byId[threadId].has_unread = false;
          draft.threads.byThreadableId[threadableId].byId[threadId].read_at =
            new Date().toISOString();
        }

        // Remove thread from unreadThreads list if present
        if (unreadThreads.includes(threadId)) {
          draft.threads.unreadThreadsByThreadableId[threadableId] = unreadThreads.filter(
            (id) => id !== threadId
          );
        }

        break;
      }

      case ChatActionTypes.SET_MESSAGES_LOADING: {
        const { threadId, isLoading } = action;
        if (!draft.messages.byThreadId[threadId]) {
          draft.messages.byThreadId[threadId] = {
            byId: {},
            allIds: [],
            loading: isLoading,
            validating: false,
            error: null,
          };
        } else {
          draft.messages.byThreadId[threadId].loading = isLoading;
          draft.messages.byThreadId[threadId].error = null;
        }
        break;
      }

      case ChatActionTypes.SET_MESSAGES_VALIDATING: {
        const { threadId, isValidating } = action;
        if (!draft.messages.byThreadId[threadId]) {
          draft.messages.byThreadId[threadId] = {
            byId: {},
            allIds: [],
            loading: false,
            validating: isValidating,
            error: null,
          };
        } else {
          draft.messages.byThreadId[threadId].validating = isValidating;
          draft.messages.byThreadId[threadId].error = null;
        }
        break;
      }

      case ChatActionTypes.SET_MESSAGES_DATA: {
        const { threadId, messages } = action;

        if (!draft.messages.byThreadId[threadId]) {
          draft.messages.byThreadId[threadId] = {
            byId: {},
            allIds: [],
            loading: false,
            validating: false,
            error: null,
          };
        }

        // Create a map of new messages by their ID
        const newMessagesById = messages.reduce(
          (acc, message) => {
            acc[message.id] = message;
            return acc;
          },
          {} as Record<string, Groupthink.MessageResource>
        );

        // Keep any existing messages with tempId (optimistically added messages)
        const currentMessages = draft.messages.byThreadId[threadId].byId;
        const tempMessages = Object.entries(currentMessages)
          .filter(([id]) => id.startsWith('temp-'))
          .reduce(
            (acc, [id, message]) => {
              acc[id] = message;
              return acc;
            },
            {} as Record<string, Groupthink.MessageResource>
          );

        // Merge temp messages with new messages
        draft.messages.byThreadId[threadId].byId = {
          ...tempMessages,
          ...newMessagesById,
        };

        // Sort all message IDs by timestamp, preserving temp message IDs
        const allMessages = Object.values(draft.messages.byThreadId[threadId].byId);
        draft.messages.byThreadId[threadId].allIds = allMessages
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          .map((message) => message.id);

        draft.messages.byThreadId[threadId].loading = false;
        draft.messages.byThreadId[threadId].validating = false;
        break;
      }

      case ChatActionTypes.SET_MESSAGES_ERROR: {
        const threadId = action.threadId;
        if (!draft.messages.byThreadId[threadId]) {
          draft.messages.byThreadId[threadId] = {
            byId: {},
            allIds: [],
            loading: false,
            validating: false,
            error: action.error.message,
          };
        } else {
          draft.messages.byThreadId[threadId].loading = false;
          draft.messages.byThreadId[threadId].validating = false;
          draft.messages.byThreadId[threadId].error = action.error.message;
        }
        break;
      }

      case ChatActionTypes.SEND_MESSAGE_OPTIMISTIC: {
        const { threadableId, threadId, tempId, content, attachments, user } = action.payload;

        if (
          !threadableId ||
          !threadId ||
          !tempId ||
          !user ||
          (!content && (!attachments || attachments?.length === 0))
        ) {
          console.error('Missing required fields for optimistic message');
          break;
        }

        // Create a temporary optimistic message
        const message = {
          id: tempId,
          content,
          attachments: attachments?.map((attachment: Attachment, index: number) => ({
            id: index,
            name: 'temp-' + index,
            mime_type: attachment.file?.type,
            url: attachment.preview,
          })),
          thread_id: threadId,
          sent_by: user,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // If this is a new thread, create a temporary thread
        const tempThreadId = `temp-thread-${tempId}`;

        if (threadId === 'new') {
          // Create a temporary thread
          const tempThread = {
            id: tempThreadId,
            name: 'New Thread',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            oldest_message: message,
          };

          // Add the temporary thread to the state
          if (!draft.threads.byThreadableId[threadableId]) {
            draft.threads.byThreadableId[threadableId] = {
              byId: {},
              allIds: [],
              loading: false,
              validating: false,
              error: null,
              hasMorePages: false,
            };
          }

          // Add thread to state
          draft.threads.byThreadableId[threadableId].byId[tempThreadId] =
            tempThread as Groupthink.ThreadResource;
          draft.threads.byThreadableId[threadableId].allIds.push(tempThreadId);

          // Update the message's thread_id to use the temporary thread ID
          message.thread_id = tempThreadId;
        }

        // Initialize thread messages if needed
        if (!draft.messages.byThreadId[message.thread_id]) {
          draft.messages.byThreadId[message.thread_id] = {
            byId: {},
            allIds: [],
            loading: false,
            validating: false,
            error: null,
          };
        }

        // Add message to thread
        draft.messages.byThreadId[message.thread_id].byId[tempId] =
          message as Groupthink.MessageResource;

        // Update thread message count
        if (draft.threads.byThreadableId[threadableId]?.byId[message.thread_id]) {
          const thread = draft.threads.byThreadableId[threadableId].byId[message.thread_id];
          thread.message_count = (thread.message_count || 0) + 1;
        }

        // Add to allIds and sort by timestamp
        const messagesById = draft.messages.byThreadId[message.thread_id].byId;
        draft.messages.byThreadId[message.thread_id].allIds = Object.values(messagesById)
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          .map((msg) => msg.id);
        break;
      }

      case ChatActionTypes.SEND_MESSAGE_CONFIRMED: {
        const { threadableId, threadId, tempId, message } = action.payload;

        if (!message.thread_id) {
          console.error('Missing threadId in SEND_MESSAGE_CONFIRMED message payload');
          break;
        }

        if (threadId === 'new') {
          // Check if this was a new thread
          if (threadableId) {
            const tempThreadId = `temp-thread-${tempId}`;
            const threadableThreads = draft.threads.byThreadableId[threadableId];

            if (threadableThreads && threadableThreads.byId[tempThreadId]) {
              // Get the temporary thread
              const tempThread = threadableThreads.byId[tempThreadId];

              // Create an updated thread with the real ID from the message, and the actual message
              const updatedThread = {
                ...tempThread,
                oldest_message: message,
                id: message.thread_id,
              };

              // Remove the temporary thread and add the updated one
              delete threadableThreads.byId[tempThreadId];
              threadableThreads.byId[message.thread_id] = updatedThread;

              // Sort threads by created_at date, newest first
              threadableThreads.allIds = Object.values(threadableThreads.byId)
                .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                .map((thread) => thread.id);

              // Move messages from temporary thread to real thread
              if (draft.messages.byThreadId[tempThreadId]) {
                const tempThreadMessages = draft.messages.byThreadId[tempThreadId];

                // Update each message's thread_id (except the temp message being replaced)
                const updatedMessagesById = Object.fromEntries(
                  Object.entries(tempThreadMessages.byId)
                    .filter(([msgId]) => msgId !== tempId)
                    .map(([msgId, msg]) => [msgId, { ...msg, thread_id: message.thread_id }])
                );

                // Add the confirmed message
                updatedMessagesById[message.id] = message;

                // Sort messages by timestamp
                const sortedMessageIds = Object.values(updatedMessagesById)
                  .sort(
                    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                  )
                  .map((msg) => msg.id);

                // Remove the temporary thread's messages
                delete draft.messages.byThreadId[tempThreadId];

                draft.messages.byThreadId[message.thread_id] = {
                  byId: updatedMessagesById,
                  allIds: sortedMessageIds,
                  loading: false,
                  validating: false,
                  error: null,
                };

                break;
              }
            }
          }
        }

        const threadMessages =
          draft.messages.byThreadId[message.thread_id] || draft.messages.byThreadId[threadId];

        if (!threadMessages) {
          // If we don't have a thread yet, create a new message collection
          draft.messages.byThreadId[message.thread_id] = {
            byId: { [message.id]: message },
            allIds: [message.id],
            loading: false,
            validating: false,
            error: null,
          };
          break;
        }

        // Remove the temp message and add the confirmed one
        delete threadMessages.byId[tempId];
        threadMessages.byId[message.id] = message;

        // Sort messages by timestamp
        threadMessages.allIds = Object.values(threadMessages.byId)
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          .map((msg) => msg.id);

        break;
      }

      case ChatActionTypes.SEND_MESSAGE_REVERT: {
        const { threadableId, threadId, tempId } = action.payload;
        const threadMessages = draft.messages.byThreadId[threadId];

        if (!threadMessages) {
          break;
        }

        // Remove the temporary message
        delete threadMessages.byId[tempId];
        threadMessages.allIds = threadMessages.allIds.filter((id) => id !== tempId);

        // Check if this was the only message in a temporary thread
        const isEmptyThread = Object.keys(threadMessages.byId).length === 0;
        const isTemporaryThread = threadId.startsWith('temp-thread-');

        if (isEmptyThread && isTemporaryThread) {
          // Remove the entire temporary thread
          delete draft.messages.byThreadId[threadId];

          // Also remove the thread from the threads collection
          const threadableThreads = draft.threads.byThreadableId[threadableId];
          if (threadableThreads) {
            delete threadableThreads.byId[threadId];
            threadableThreads.allIds = threadableThreads.allIds.filter((id) => id !== threadId);
          }
        } else {
          // Update thread message count
          if (draft.threads.byThreadableId[threadableId]?.byId[threadId]) {
            const thread = draft.threads.byThreadableId[threadableId].byId[threadId];
            thread.message_count = Math.max(0, (thread.message_count || 1) - 1);
          }
        }

        break;
      }

      case ChatActionTypes.HANDLE_MESSAGE_SENT_EVENT: {
        const { message } = action.payload;
        const user = action.user;

        // In RN, this payload is sometimes a string!?
        let messageData = message as Groupthink.MessageResource;
        if (typeof message === 'string') {
          try {
            messageData = JSON.parse(message) as Groupthink.MessageResource;
          } catch (error) {
            console.error('Failed to parse message:', error);
            break;
          }
        }

        const threadId = messageData.thread_id;
        const threadableType = messageData.threadable_key;
        const threadableId = messageData.threadable_id;
        const messageId = messageData.id;

        const sender = messageData.sent_by;

        if (!threadId || !messageId || !user) {
          console.error('Missing threadId, messageId, or user in HANDLE_MESSAGE_SENT_EVENT', {
            messageData,
            threadId,
            messageId,
            user,
          });
          break;
        }

        if (String(sender.id) === String(user.id)) {
          // This is our own message, so we can skip it
          break;
        }

        // Check if we already have a thread with this threadId
        if (threadableId && draft.threads.byThreadableId[threadableId]?.byId[threadId]) {
          // Thread exists - check if the message already exists in the thread
          const threadMessages = draft.messages.byThreadId[threadId] || {
            byId: {},
            allIds: [],
            loading: false,
            validating: false,
            error: null,
          };

          // If the message doesn't already exist in the thread, add it
          if (!threadMessages.byId[messageId]) {
            // Add the message to the thread's messages
            threadMessages.byId[messageId] = messageData;

            // Sort messages by timestamp
            threadMessages.allIds = Object.values(threadMessages.byId)
              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
              .map((msg) => msg.id);

            // Update message collection for this thread
            draft.messages.byThreadId[threadId] = threadMessages;

            // Update thread's message count
            if (draft.threads.byThreadableId[threadableId]?.byId[threadId]) {
              const thread = draft.threads.byThreadableId[threadableId].byId[threadId];
              thread.message_count = (thread.message_count || 0) + 1;
            }
          }
        } else if (threadableId) {
          // Thread doesn't exist - create a new thread with this message

          // Create a basic thread with this message as the oldest_message
          const newThread = {
            id: threadId,
            name: 'Thread', // Default name
            threadable_id: threadableId,
            threadable_type: threadableType,
            created_at: messageData.created_at,
            updated_at: messageData.updated_at,
            oldest_message: messageData,
            message_count: 1,
          };

          // Initialize the threads collection for this room if needed
          if (!draft.threads.byThreadableId[threadableId]) {
            draft.threads.byThreadableId[threadableId] = {
              byId: {},
              allIds: [],
              loading: false,
              validating: false,
              error: null,
              hasMorePages: false,
            };
          }

          // Add thread to the room's threads
          draft.threads.byThreadableId[threadableId].byId[threadId] =
            newThread as Groupthink.ThreadResource;
          draft.threads.byThreadableId[threadableId].allIds.push(threadId);

          // Initialize the messages collection for this thread
          draft.messages.byThreadId[threadId] = {
            byId: { [messageId]: messageData },
            allIds: [messageId],
            loading: false,
            validating: false,
            error: null,
          };
        }

        break;
      }

      case ChatActionTypes.HANDLE_MESSAGE_REACTED_TO_EVENT: {
        const { message } = action.payload;
        const threadId = message.thread_id;
        const threadableType = message.threadable_key;
        const threadableId = message.threadable_id;
        const messageId = message.id;

        if (!threadId || !messageId) {
          console.error('Missing threadId or messageId in HANDLE_MESSAGE_REACTED_TO_EVENT');
          break;
        }

        // Check if we already have a thread with this threadId
        if (threadableId && draft.threads.byThreadableId[threadableId]?.byId[threadId]) {
          // Thread exists - check if the message already exists
          const threadMessages = draft.messages.byThreadId[threadId] || {
            byId: {},
            allIds: [],
            loading: false,
            validating: false,
            error: null,
          };

          // Replace the message if it exists or add it if it doesn't
          threadMessages.byId[messageId] = message;

          // Sort messages by timestamp
          threadMessages.allIds = Object.values(threadMessages.byId)
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
            .map((msg) => msg.id);

          // Update message collection for this thread
          draft.messages.byThreadId[threadId] = threadMessages;

          // Check if this message is the oldest message in the thread and update it
          const thread = draft.threads.byThreadableId[threadableId].byId[threadId];
          if (thread.oldest_message.id === messageId) {
            thread.oldest_message = message;
          }
        } else if (threadableId) {
          // Thread doesn't exist - create a new thread with this message

          // Create a basic thread with this message as the oldest_message
          const newThread = {
            id: threadId,
            name: 'New Thread', // Default name
            threadable_id: threadableId,
            threadable_type: threadableType,
            created_at: message.created_at,
            updated_at: message.updated_at,
            oldest_message: message,
            message_count: 1,
          };

          // Initialize the threads collection for this room if needed
          if (!draft.threads.byThreadableId[threadableId]) {
            draft.threads.byThreadableId[threadableId] = {
              byId: {},
              allIds: [],
              loading: false,
              validating: false,
              error: null,
              hasMorePages: false,
            };
          }

          // Add thread to the room's threads
          draft.threads.byThreadableId[threadableId].byId[threadId] =
            newThread as Groupthink.ThreadResource;
          draft.threads.byThreadableId[threadableId].allIds.push(threadId);

          // Initialize the messages collection for this thread
          draft.messages.byThreadId[threadId] = {
            byId: { [messageId]: message },
            allIds: [messageId],
            loading: false,
            validating: false,
            error: null,
          };
        }

        break;
      }

      case ChatActionTypes.ADD_REACTION_OPTIMISTIC: {
        const { threadableId, threadId, messageId, tempId, content, user } = action.payload;

        // Create a new reaction object with the temporary ID
        const optimisticReaction = {
          id: tempId,
          message_id: messageId,
          content: content,
          sent_by: user as unknown, // AssistantResource !== UserResource
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // check if the oldest_message in the thread is messageID - if it is, we are updating THAT thread and ALSO the message with that id
        const thread = draft.threads.byThreadableId[threadableId]?.byId[threadId];
        if (thread && thread.oldest_message.id === messageId) {
          const existingReactions = thread.oldest_message.reactions || [];

          // Check if reaction already exists for this user and content
          const hasExistingReaction = existingReactions.some(
            (reaction) =>
              String(reaction.sent_by.id) === String(user.id) && reaction.content === content
          );

          if (!hasExistingReaction) {
            thread.oldest_message.reactions = [
              ...existingReactions,
              optimisticReaction as Groupthink.MessageReactionResource,
            ];
          }
        }

        const threadMessages = draft.messages.byThreadId[threadId];
        if (threadMessages && threadMessages.byId[messageId]) {
          const message = threadMessages.byId[messageId];
          const existingReactions = message.reactions || [];

          // Check if reaction already exists for this user and content
          const hasExistingReaction = existingReactions.some(
            (reaction) =>
              String(reaction.sent_by.id) === String(user.id) && reaction.content === content
          );

          if (!hasExistingReaction) {
            // Add the reaction to the message
            message.reactions = [
              ...existingReactions,
              optimisticReaction as Groupthink.MessageReactionResource,
            ];
          }
        }

        break;
      }

      case ChatActionTypes.ADD_REACTION_CONFIRMED: {
        // The optimistic update already added the reaction, so no additional changes needed
        break;
      }

      case ChatActionTypes.ADD_REACTION_REVERT: {
        const { threadId, messageId, tempId } = action.payload;

        const threadMessages = draft.messages.byThreadId[threadId];
        if (!threadMessages) {
          break;
        }

        const message = threadMessages.byId[messageId];
        if (!message) {
          break;
        }

        // Remove the temporary reaction
        message.reactions = (message.reactions || []).filter((r) => r.id !== tempId);

        break;
      }

      case ChatActionTypes.REMOVE_REACTION_OPTIMISTIC: {
        const { threadableId, threadId, messageId, content, user } = action.payload;

        if (!user) {
          console.error('Missing user in REMOVE_REACTION_OPTIMISTIC');
          break;
        }

        const thread = draft.threads.byThreadableId[threadableId]?.byId[threadId];
        if (thread && thread.oldest_message.id === messageId) {
          // Filter out the specific reaction that matches both user ID and content
          thread.oldest_message.reactions = (thread.oldest_message.reactions || []).filter(
            (reaction) => {
              // Keep reactions that don't match both the user ID and content
              return !(
                String(reaction.sent_by.id) === String(user.id) && reaction.content === content
              );
            }
          );
        }

        const threadMessages = draft.messages.byThreadId[threadId];
        if (threadMessages && threadMessages.byId[messageId]) {
          const message = threadMessages.byId[messageId];

          // Filter out the specific reaction from the message that matches both user ID and content
          message.reactions = (message.reactions || []).filter((reaction) => {
            // Keep reactions that don't match both the user ID and content
            return !(
              String(reaction.sent_by.id) === String(user.id) && reaction.content === content
            );
          });
        }

        break;
      }

      case ChatActionTypes.REMOVE_REACTION_CONFIRMED: {
        // The optimistic update already removed the reaction, so no additional changes needed
        break;
      }

      case ChatActionTypes.REMOVE_REACTION_REVERT: {
        const { threadableId, threadId, messageId, content, user } = action.payload;

        // Create the reaction to add back
        const revertedReaction = {
          id: `revert-${Date.now()}`, // Generate a temporary ID
          message_id: messageId,
          content: content,
          sent_by: user,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Check if we need to update the thread's oldest_message
        const thread = draft.threads.byThreadableId[threadableId]?.byId[threadId];
        if (thread && thread.oldest_message.id === messageId) {
          thread.oldest_message.reactions = [
            // @ts-ignore
            ...(thread.oldest_message.reactions || []),
            // @ts-ignore
            revertedReaction,
          ];
        }

        // Update the message in the messages collection
        const threadMessages = draft.messages.byThreadId[threadId];
        if (threadMessages && threadMessages.byId[messageId]) {
          const message = threadMessages.byId[messageId];
          // @ts-ignore
          message.reactions = [...(message.reactions || []), revertedReaction];
        }

        break;
      }

      case ChatActionTypes.DELETE_THREAD: {
        const { threadableId, threadId } = action.payload;

        delete draft.threads.byThreadableId[threadableId]?.byId[threadId];
        draft.threads.byThreadableId[threadableId].allIds = draft.threads.byThreadableId[
          threadableId
        ].allIds.filter((id) => id !== threadId);
        delete draft.messages.byThreadId[threadId];

        break;
      }

      case ChatActionTypes.DELETE_MESSAGE: {
        const { threadableId, threadId, messageId } = action.payload;

        const thread = draft.threads.byThreadableId[threadableId]?.byId[threadId];
        const isDeletingOldestMessage =
          thread && thread.oldest_message && thread.oldest_message.id === messageId;

        if (isDeletingOldestMessage) {
          // We are deleting the oldest message in the thread
          const threadMessages = draft.messages.byThreadId[threadId];

          // Check if there are other messages in the thread
          if (threadMessages && threadMessages.allIds.length > 1) {
            // Find the next message in the thread (chronologically)
            // Remove the current oldest message from consideration
            const remainingMessages = Object.values(threadMessages.byId)
              .filter((msg) => msg.id !== messageId)
              .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

            if (remainingMessages.length > 0) {
              // Set the next oldest message as the new oldest_message for the thread
              const newOldestMessage = remainingMessages[0];

              thread.oldest_message = newOldestMessage;
              thread.message_count = Math.max(0, (thread?.message_count || 1) - 1);

              delete threadMessages.byId[messageId];
              threadMessages.allIds = threadMessages.allIds.filter((id) => id !== messageId);

              break;
            }
          }

          // If there are no other messages, delete the thread (original behavior)
          delete draft.threads.byThreadableId[threadableId].byId[threadId];
          draft.threads.byThreadableId[threadableId].allIds = draft.threads.byThreadableId[
            threadableId
          ].allIds.filter((id) => id !== threadId);
          delete draft.messages.byThreadId[threadId];

          break;
        }

        // Otherwise, remove just the message from the thread state
        const threadMessages = draft.messages.byThreadId[threadId];
        if (!threadMessages || !threadMessages.byId[messageId]) {
          break;
        }

        // Remove the message from the collection
        delete threadMessages.byId[messageId];
        threadMessages.allIds = threadMessages.allIds.filter((id) => id !== messageId);

        // Update thread message count
        if (draft.threads.byThreadableId[threadableId]?.byId[threadId]) {
          const thread = draft.threads.byThreadableId[threadableId].byId[threadId];
          thread.message_count = Math.max(0, (thread?.message_count || 1) - 1);
        }

        break;
      }

      case ChatActionTypes.EDIT_MESSAGE: {
        const { threadableId, threadId, messageId, content, updatedMessage } = action.payload;

        const thread = draft.threads.byThreadableId[threadableId]?.byId[threadId];

        // Check if we're editing the oldest message in the thread
        if (thread && thread.oldest_message && thread.oldest_message.id === messageId) {
          // Update the thread's oldest_message
          thread.oldest_message.content = content;
          thread.oldest_message.updated_at = updatedMessage.updated_at;
        }

        // Update the message in the thread's messages collection
        const threadMessages = draft.messages.byThreadId[threadId];
        if (!threadMessages || !threadMessages.byId[messageId]) {
          break;
        }

        threadMessages.byId[messageId].content = content;
        threadMessages.byId[messageId].updated_at = updatedMessage.updated_at;

        break;
      }

      default:
        break;
    }
  });
}
